import { useState } from "react";
import { api } from "@/lib/api";
import { CalendarRange, ChevronDown, ChevronUp, Radio, CheckCircle2, ListChecks } from "lucide-react";

const QUICK_EMOJIS = ["👍", "✅", "🎉", "❤️", "🙌"];

/**
 * MeetingDigestCard — renders a `meeting_digest` message: a weekly project
 * recap of meetings, decisions and open action items.
 */
export default function MeetingDigestCard({ message, myId }) {
  const md = message?.metadata || {};
  const [open, setOpen] = useState(true);
  const [reactions, setReactions] = useState(message?.reactions || {});
  const [showPicker, setShowPicker] = useState(false);
  const meetings = Array.isArray(md.meetings) ? md.meetings : [];
  const decisions = Array.isArray(md.decisions) ? md.decisions : [];
  const openTasks = Array.isArray(md.open_tasks) ? md.open_tasks : [];

  const react = async (emoji) => {
    setShowPicker(false);
    setReactions((prev) => {
      const next = { ...prev };
      const users = new Set(next[emoji] || []);
      if (myId && users.has(myId)) users.delete(myId); else if (myId) users.add(myId);
      if (users.size) next[emoji] = Array.from(users); else delete next[emoji];
      return next;
    });
    try { await api.post(`/messages/${message.id}/react`, { emoji }); } catch { /* poll reconciles */ }
  };

  const reactionEntries = Object.entries(reactions).filter(([, u]) => (u || []).length > 0);

  return (
    <div className="my-3 mx-auto w-full max-w-[560px] px-2" data-testid={`meeting-digest-${message.id}`}>
      <div className="rounded-2xl border border-brand/25 bg-gradient-to-b from-brand/[0.06] to-transparent overflow-hidden">
        <button
          type="button"
          data-testid={`meeting-digest-toggle-${message.id}`}
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left"
        >
          <div className="w-9 h-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
            <CalendarRange className="w-[18px] h-[18px] text-brand" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-ink">Weekly recap</div>
            <div className="text-[11px] text-ink-mute">
              {md.meeting_count || 0} meeting{(md.meeting_count || 0) === 1 ? "" : "s"} · {decisions.length} decision{decisions.length === 1 ? "" : "s"} · {md.open_task_count || 0} open task{(md.open_task_count || 0) === 1 ? "" : "s"}
            </div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-ink-mute" /> : <ChevronDown className="w-4 h-4 text-ink-mute" />}
        </button>

        {open && (
          <div className="px-4 pb-4 space-y-3">
            {meetings.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute mb-1.5">Meetings this week</div>
                <ul className="space-y-1.5">
                  {meetings.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink-dim">
                      <Radio className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" />
                      <span className="leading-snug"><span className="text-ink font-medium">{m.title}</span>{m.tldr ? ` — ${m.tldr}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {decisions.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute mb-1.5">Decisions</div>
                <ul className="space-y-1.5">
                  {decisions.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink-dim">
                      <CheckCircle2 className="w-3.5 h-3.5 text-tn-green mt-0.5 shrink-0" />
                      <span className="leading-snug">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-mute mb-1.5">Open action items ({md.open_task_count || 0})</div>
              {openTasks.length === 0 ? (
                <p className="text-[13px] text-ink-mute">Nothing open 🎉</p>
              ) : (
                <ul className="space-y-1.5">
                  {openTasks.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink-dim">
                      <ListChecks className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" />
                      <span className="leading-snug flex-1">{t.title}</span>
                      {t.assignee_name && <span className="text-[11px] text-ink-mute shrink-0">{t.assignee_name}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 flex-wrap px-4 py-2.5 border-t border-brand/15" data-testid={`meeting-digest-reactions-${message.id}`}>
          {reactionEntries.map(([emoji, users]) => {
            const mine = myId && (users || []).includes(myId);
            return (
              <button
                key={emoji}
                type="button"
                data-testid={`meeting-digest-reaction-${message.id}-${emoji}`}
                onClick={() => react(emoji)}
                className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[12px] ring-1 transition-colors ${
                  mine ? "bg-brand/15 ring-brand/40 text-ink" : "ring-hairline text-ink-dim hover:bg-white/5"
                }`}
              >
                <span>{emoji}</span><span className="font-semibold">{(users || []).length}</span>
              </button>
            );
          })}
          <div className="relative">
            <button
              type="button"
              data-testid={`meeting-digest-react-btn-${message.id}`}
              onClick={() => setShowPicker((v) => !v)}
              className="w-7 h-7 flex items-center justify-center rounded-full ring-1 ring-hairline text-ink-mute hover:bg-white/5 text-[13px]"
              aria-label="React"
            >
              ☺+
            </button>
            {showPicker && (
              <div className="absolute left-0 bottom-9 z-20 flex items-center gap-1 bg-surface2 ring-1 ring-hairline rounded-full px-2 py-1.5 shadow-xl" data-testid={`meeting-digest-emoji-picker-${message.id}`}>
                {QUICK_EMOJIS.map((e) => (
                  <button key={e} type="button" onClick={() => react(e)} className="text-[16px] hover:scale-125 transition-transform" data-testid={`meeting-digest-pick-${message.id}-${e}`}>{e}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
